import { useState, useMemo } from 'react';
import { Scissors, Plus, Trash2, Pencil, Package, Check, Search, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import { Product } from '@/types';
import { useProducts, useCreateProduct, useUpdateProduct, useDeleteProduct } from '@/hooks/useProducts';
import { useCreateStockMovement } from '@/hooks/useInventory';
import { formatPrice, parseARNumber, formatARNumber } from '@/utils/format';
import { genInternalBarcode } from '@/utils/barcode';
import { matchesAllWords } from '@/utils/search';
import CurrencyInput from '@/components/ui/CurrencyInput';

// Costo final por unidad base (por gramo para granel): base*(1+iva%) + flete + impuestos + comisiones
function landedCostPerBase(p?: Product): number {
  if (!p || p.cost_price == null) return 0;
  const base = Number(p.cost_price) || 0;
  const ivaPct = p.iva_rate != null ? Number(p.iva_rate) : 0;
  const freight = Number(p.freight_cost) || 0;
  const tax = Number(p.tax_cost) || 0;
  const commission = Number(p.commission_cost) || 0;
  return base * (1 + ivaPct / 100) + freight + tax + commission;
}

interface FracForm {
  name: string;
  code: string;
  source_product_id?: number;
  grams: string;        // gramos por paquete
  price_cash: string;   // precio de venta del paquete
}

// Una fila del alta múltiple: cada una termina siendo un producto fraccionado
interface FracRow {
  id: number;
  name: string;
  grams: string;
  price: string;
  code: string;
  nameTouched: boolean;  // si escribieron el nombre a mano, no se autocompleta más
}

// Presentaciones típicas, para cargar rápido
const GRAM_PRESETS = [250, 500, 1000, 2000];

// "Nueces 500 g" / "Nueces 1 kg"
const packageLabel = (parentName: string, grams: number): string => {
  if (!grams) return parentName;
  const size = grams >= 1000 && grams % 100 === 0
    ? `${(grams / 1000).toLocaleString('es-AR', { maximumFractionDigits: 2 })} kg`
    : `${grams} g`;
  return `${parentName} ${size}`;
};

const emptyForm: FracForm = {
  name: '',
  code: '',
  source_product_id: undefined,
  grams: '',
  price_cash: '',
};

const emptyRow = (id: number): FracRow =>
  ({ id, name: '', grams: '', price: '', code: '', nameTouched: false });

export default function Fraccionados() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [form, setForm] = useState<FracForm>(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState<Product | null>(null);

  // El fraccionado puede ser un paquete nuevo o un producto que YA existe en el
  // catálogo (ahí no se crea nada: se le asigna el padre y pasa a descontar de él).
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [linkedProduct, setLinkedProduct] = useState<Product | null>(null);
  const [linkSearch, setLinkSearch] = useState('');

  // Buscador del padre: con el catálogo entero, un desplegable es inusable
  const [parentSearch, setParentSearch] = useState('');

  // Buscador de la lista de fraccionados
  const [search, setSearch] = useState('');

  // Carga rapida del stock del GRANEL PADRE desde esta pantalla. El fraccionado no
  // tiene stock propio (track_stock=false): lo que se ajusta es el granel del que sale.
  const createMovement = useCreateStockMovement();
  const [editStockId, setEditStockId] = useState<number | null>(null);
  const [stockKg, setStockKg] = useState('');
  const [savingStock, setSavingStock] = useState(false);

  const openEditStock = (frac: Product) => {
    setEditStockId(frac.id);
    setStockKg(formatARNumber(Number(frac.source_current_stock ?? 0) / 1000));
  };

  const saveStockPadre = async (frac: Product) => {
    const padreId = frac.source_product_id;
    if (!padreId) return;
    const nuevoKg = parseARNumber(stockKg);
    if (nuevoKg === null || isNaN(nuevoKg)) {
      setEditStockId(null);
      return;
    }
    const actualG = Number(frac.source_current_stock ?? 0);
    // El stock del granel vive en gramos; el usuario carga el total final en kg.
    const deltaG = Math.round(nuevoKg * 1000) - actualG;
    if (deltaG === 0) {
      setEditStockId(null);
      return;
    }
    setSavingStock(true);
    try {
      await createMovement.mutateAsync({
        product_id: padreId,
        movement_type: 'adjustment',
        quantity: deltaG,
        quantity_unit: 'gram',
        notes: `Ajuste desde Fraccionados: ${formatARNumber(actualG / 1000)} kg -> ${formatARNumber(nuevoKg)} kg`,
      });
      toast.success('Stock del granel actualizado');
      setEditStockId(null);
    } catch {
      toast.error('No se pudo actualizar el stock');
    } finally {
      setSavingStock(false);
    }
  };

  // Alta de varias presentaciones del MISMO padre de una sola vez: se elige el
  // producto a granel una vez y se cargan 250 g, 500 g, 1 kg… todas juntas.
  const [rows, setRows] = useState<FracRow[]>([]);
  const [nextRowId, setNextRowId] = useState(1);
  const [savingRows, setSavingRows] = useState(false);

  // Solo activos: al borrar (soft delete) el fraccionado desaparece de la lista.
  const { data: allProducts = [], isLoading } = useProducts({ only_active: true, is_combo: false });

  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();

  // Fraccionados = productos con producto padre asignado
  const fraccionados = useMemo(
    () => allProducts
      .filter((p) => p.source_product_id)
      .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })),
    [allProducts]
  );

  // Padres posibles: productos a granel (peso) que NO sean a su vez fraccionados
  const parentOptions = useMemo(
    () =>
      allProducts
        .filter((p) => p.dimension === 'weight' && !p.source_product_id && p.is_active)
        .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })),
    [allProducts]
  );

  const parentResults = useMemo(() => {
    const q = parentSearch.trim();
    return q ? parentOptions.filter((p) => matchesAllWords(q, p.name, p.code)) : parentOptions;
  }, [parentOptions, parentSearch]);

  const productById = useMemo(() => {
    const m = new Map<number, Product>();
    allProducts.forEach((p) => m.set(p.id, p));
    return m;
  }, [allProducts]);

  const parent = form.source_product_id ? productById.get(form.source_product_id) : undefined;
  const gramsNum = parseFloat(form.grams) || 0;
  const cashPriceNum = parseARNumber(form.price_cash) ?? 0;

  // Costo del paquete = costo por gramo del padre × gramos del paquete
  const costPerGram = landedCostPerBase(parent);
  const fracCost = useMemo(() => costPerGram * gramsNum, [costPerGram, gramsNum]);
  const fracProfit = cashPriceNum - fracCost;
  const fracMarkupPct = fracCost > 0 ? (fracProfit / fracCost) * 100 : 0;
  const fracMarginPct = cashPriceNum > 0 ? (fracProfit / cashPriceNum) * 100 : 0;

  // Cuántos paquetes se pueden armar con el stock actual del padre (padre en gramos base)
  const parentStockGrams = parent ? Number(parent.current_stock ?? 0) : 0;
  const possiblePackages = gramsNum > 0 ? Math.floor(parentStockGrams / gramsNum) : 0;

  // Productos que se pueden convertir en fraccionado: los que no son a granel ni
  // ya fraccionados (un producto suelto que en realidad sale de un granel).
  const linkableProducts = useMemo(
    () =>
      allProducts
        .filter((p) => p.dimension !== 'weight' && !p.source_product_id && p.is_active)
        .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })),
    [allProducts]
  );

  const linkResults = useMemo(() => {
    const q = linkSearch.trim();
    return q ? linkableProducts.filter((p) => matchesAllWords(q, p.name, p.code)) : [];
  }, [linkableProducts, linkSearch]);

  // Presentaciones que este padre YA tiene: se muestran al elegirlo, para agregar
  // las que faltan sin repetir ninguna.
  const parentFractions = useMemo(() => {
    if (!parent) return [];
    return fraccionados
      .filter((f) => f.source_product_id === parent.id && f.fraction_grams != null)
      .map((f) => ({ id: f.id, name: f.name, grams: Math.round(Number(f.fraction_grams)) }))
      .sort((a, b) => a.grams - b.grams);
  }, [fraccionados, parent]);

  const existingGrams = useMemo(
    () => new Set(parentFractions.map((f) => f.grams)),
    [parentFractions]
  );

  // La lista filtrada. Busca también por el nombre del padre y por los gramos,
  // que es como uno se acuerda de un fraccionado ("nueces 500").
  const fraccionadosFiltrados = useMemo(() => {
    const q = search.trim();
    if (!q) return fraccionados;
    return fraccionados.filter((f) => {
      const padre = f.source_product_id ? productById.get(f.source_product_id) : undefined;
      const gramos = f.fraction_grams != null ? String(Number(f.fraction_grams)) : '';
      return matchesAllWords(q, f.name, f.code, padre?.name, gramos);
    });
  }, [fraccionados, search, productById]);

  // === Alta múltiple ===
  const isMulti = !editing && mode === 'new';

  const addRow = (grams?: number) => {
    setRows((rs) => [
      ...rs,
      {
        ...emptyRow(nextRowId),
        name: parent && grams ? packageLabel(parent.name, grams) : '',
        grams: grams ? String(grams) : '',
      },
    ]);
    setNextRowId((n) => n + 1);
  };

  const updateRow = (id: number, patch: Partial<FracRow>) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const removeRow = (id: number) => setRows((rs) => rs.filter((r) => r.id !== id));

  // Al cambiar los gramos se rearma el nombre, salvo que lo hayan escrito a mano
  const onRowGramsChange = (row: FracRow, grams: string) => {
    const g = parseFloat(grams) || 0;
    updateRow(row.id, {
      grams,
      name: row.nameTouched ? row.name : (parent && g > 0 ? packageLabel(parent.name, g) : row.name),
    });
  };

  const validRows = rows.filter(
    (r) => r.name.trim() && (parseFloat(r.grams) || 0) > 0 && (parseARNumber(r.price) ?? 0) > 0
  );

  const openCreate = () => {
    setEditing(null);
    setMode('new');
    setLinkedProduct(null);
    setLinkSearch('');
    setParentSearch('');
    setForm(emptyForm);
    setRows([emptyRow(1)]);
    setNextRowId(2);
    setIsModalOpen(true);
  };

  const openEdit = (frac: Product) => {
    setEditing(frac);
    setMode('new');       // editando ya hay un producto: el modo no aplica
    setLinkedProduct(null);
    setParentSearch('');
    setForm({
      name: frac.name,
      code: frac.code ?? '',
      source_product_id: frac.source_product_id ?? undefined,
      grams: frac.fraction_grams != null ? String(Number(frac.fraction_grams)) : '',
      price_cash: frac.sale_price != null ? formatARNumber(Number(frac.sale_price)) : '',
    });
    setIsModalOpen(true);
  };

  const updateForm = (patch: Partial<FracForm>) => setForm((f) => ({ ...f, ...patch }));

  // Al elegir padre, autocompletar el nombre si está vacío y rearmar las filas
  const onSelectParent = (id?: number) => {
    const p = id ? productById.get(id) : undefined;
    setForm((f) => ({
      ...f,
      source_product_id: id,
      name: f.name.trim() ? f.name : (p ? p.name : f.name),
    }));
    if (p) {
      setRows((rs) =>
        rs.map((r) => {
          const g = parseFloat(r.grams) || 0;
          return r.nameTouched || g <= 0 ? r : { ...r, name: packageLabel(p.name, g) };
        })
      );
    }
  };

  // Al elegir un producto existente, se traen sus datos al formulario
  const onSelectLinked = (p: Product) => {
    setLinkedProduct(p);
    setForm((f) => ({
      ...f,
      name: p.name,
      code: p.code ?? '',
      price_cash: p.sale_price != null ? formatARNumber(Number(p.sale_price)) : f.price_cash,
    }));
  };

  const isValid = isMulti
    ? !!form.source_product_id && validRows.length > 0
    : form.name.trim().length > 0 &&
      !!form.source_product_id &&
      gramsNum > 0 &&
      cashPriceNum > 0 &&
      (editing !== null || !!linkedProduct);

  // Crea de una sola vez todas las presentaciones cargadas para el mismo padre
  const handleSaveRows = async () => {
    if (!parent || validRows.length === 0) return;

    setSavingRows(true);
    const fallaron: string[] = [];
    let creados = 0;

    for (const row of validRows) {
      try {
        await createProduct.mutateAsync({
          name: row.name.trim(),
          code: row.code.trim() || undefined,
          is_combo: false,
          source_product_id: parent.id,
          fraction_grams: parseFloat(row.grams) || 0,
          dimension: 'count' as const,
          display_unit: 'unit' as const,
          track_stock: false,
          sale_price: parseARNumber(row.price) ?? 0,
          category_id: parent.category_id ?? undefined,
          supplier_id: parent.supplier_id ?? undefined,
        });
        creados += 1;
      } catch (e: any) {
        const detail = e?.response?.data?.detail;
        fallaron.push(`${row.name.trim()}: ${typeof detail === 'string' ? detail : 'error al crear'}`);
      }
    }

    setSavingRows(false);

    if (creados > 0) {
      toast.success(creados === 1 ? 'Fraccionado creado' : `${creados} fraccionados creados`);
    }
    if (fallaron.length > 0) {
      // Quedan en el modal SOLO los que fallaron, para corregirlos sin volver a
      // cargar los que ya entraron.
      toast.error(fallaron.join(' · '));
      setRows((rs) => rs.filter((r) => fallaron.some((f) => f.startsWith(`${r.name.trim()}:`))));
      return;
    }

    setIsModalOpen(false);
    setRows([]);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!isValid || !parent) return;
    if (isMulti) return handleSaveRows();

    const payload = {
      name: form.name.trim(),
      code: form.code.trim() || undefined,
      is_combo: false,
      // Fraccionado: consume del padre (granel). El backend fuerza track_stock=false
      // y calcula el costo automáticamente desde el costo por gramo del padre.
      source_product_id: form.source_product_id,
      fraction_grams: gramsNum,
      dimension: 'count' as const,
      display_unit: 'unit' as const,
      track_stock: false,
      sale_price: cashPriceNum,
      // Un paquete nuevo hereda categoría y proveedor del padre para que agrupen
      // juntos. Si es un producto que ya existía, se respeta su clasificación.
      ...(mode === 'existing' && !editing
        ? {}
        : {
            category_id: parent.category_id ?? undefined,
            supplier_id: parent.supplier_id ?? undefined,
          }),
    };

    try {
      if (editing) {
        await updateProduct.mutateAsync({ id: editing.id, data: payload });
      } else if (mode === 'existing' && linkedProduct) {
        // No se crea nada: al producto que ya existía se le asigna el padre
        await updateProduct.mutateAsync({ id: linkedProduct.id, data: payload });
        toast.success(`${linkedProduct.name} ahora descuenta de ${parent.name}`);
      } else {
        await createProduct.mutateAsync(payload);
      }
      setIsModalOpen(false);
      setForm(emptyForm);
      setEditing(null);
      setLinkedProduct(null);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo guardar el fraccionado');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteProduct.mutateAsync(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (e) {
      console.error('Error eliminando fraccionado:', e);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-taupe-800 flex items-center gap-2">
            <Scissors className="h-6 w-6 text-teal-600" />
            Fraccionados
          </h1>
          <p className="text-taupe-500">
            Vendé un producto a granel en paquetes (ej. Nueces 250g). Consume del stock del padre.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-teal-600 hover:bg-teal-700">
          <Plus className="h-5 w-5 mr-2" />
          Nuevo fraccionado
        </Button>
      </div>

      {/* Buscador */}
      {fraccionados.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-taupe-400" />
          <input
            type="text"
            placeholder="Buscar por nombre, código, producto padre o gramos…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-11 pr-10 py-2.5 border-2 border-cream-200 rounded-xl text-base bg-white focus:outline-none focus:border-taupe-400"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-taupe-400 hover:text-taupe-600"
              title="Limpiar"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {/* Lista */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-taupe-600 mx-auto"></div>
        </div>
      ) : fraccionadosFiltrados.length === 0 ? (
        <div className="bg-white rounded-2xl border border-cream-200 text-center py-16">
          <Scissors className="h-16 w-16 text-taupe-300 mx-auto mb-4" />
          <p className="text-taupe-600 font-medium">
            {search.trim() ? 'Ningún fraccionado coincide con la búsqueda' : 'No hay fraccionados creados'}
          </p>
          <p className="text-taupe-400 text-sm">
            {search.trim()
              ? 'Probá con otras palabras'
              : 'Elegí un producto a granel y creá paquetes con gramos y precio fijos'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-cream-200 overflow-hidden">
          {/* Encabezado (desktop) */}
          <div className="hidden md:grid grid-cols-[80px_1fr_1fr_130px_130px_120px] items-center gap-3 px-4 py-2.5 bg-cream-100 border-b border-cream-200 text-xs uppercase tracking-wide font-semibold text-taupe-600">
            <span>Gramos</span>
            <span>Nombre</span>
            <span>Sale de (padre)</span>
            <span className="text-right">Precio</span>
            <span className="text-right">Stock padre</span>
            <span className="text-right">Acciones</span>
          </div>
          {search.trim() && (
            <p className="px-4 py-1.5 text-xs text-taupe-500 bg-cream-50 border-b border-cream-200">
              {fraccionadosFiltrados.length} de {fraccionados.length}
            </p>
          )}
          <div className="divide-y divide-cream-100">
            {fraccionadosFiltrados.map((frac) => {
              const padre = frac.source_product_id ? productById.get(frac.source_product_id) : undefined;
              return (
                <div
                  key={frac.id}
                  className="grid grid-cols-2 md:grid-cols-[80px_1fr_1fr_130px_130px_120px] items-center gap-x-3 gap-y-1 px-4 py-3"
                >
                  {/* Gramos */}
                  <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-semibold justify-self-start">
                    {frac.fraction_grams != null ? `${Number(frac.fraction_grams)} g` : 'FRAC'}
                  </span>
                  {/* Nombre */}
                  <div className="min-w-0 col-span-2 md:col-span-1 order-last md:order-none">
                    <p className="font-medium text-taupe-800 truncate" title={frac.name}>
                      {frac.name}
                    </p>
                    {frac.code && <p className="text-xs text-taupe-400 truncate">{frac.code}</p>}
                  </div>
                  {/* Padre */}
                  <div className="text-sm text-taupe-600 flex items-center gap-1 min-w-0">
                    <Package className="h-3.5 w-3.5 text-taupe-400 flex-shrink-0" />
                    <span className="truncate">{padre?.name ?? '—'}</span>
                  </div>
                  {/* Precio */}
                  <p className="text-base font-bold text-green-600 md:text-right justify-self-end md:justify-self-auto">
                    {formatPrice(frac.sale_price ?? 0)}
                  </p>
                  {/* Stock padre — editable: ajusta el stock del GRANEL, no del paquete */}
                  <div className="text-sm md:text-right">
                    {editStockId === frac.id ? (
                      <div className="flex items-center gap-1 md:justify-end">
                        <input
                          autoFocus
                          value={stockKg}
                          onChange={(e) => setStockKg(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveStockPadre(frac);
                            if (e.key === 'Escape') setEditStockId(null);
                          }}
                          className="w-16 px-2 py-1 text-sm text-right border border-teal-400 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-400"
                        />
                        <span className="text-xs text-taupe-500">kg</span>
                        <button
                          onClick={() => saveStockPadre(frac)}
                          disabled={savingStock}
                          title="Guardar"
                          className="p-1 rounded bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-50"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setEditStockId(null)}
                          title="Cancelar"
                          className="p-1 rounded bg-red-50 text-red-500 hover:bg-red-100"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => openEditStock(frac)}
                        title={`Actualizar el stock de "${padre?.name ?? 'el granel'}"`}
                        className="md:text-right hover:bg-cream-100 rounded-lg px-2 py-0.5 transition-colors w-full md:w-auto"
                      >
                        <span
                          className={`font-semibold ${
                            Number(frac.source_current_stock ?? 0) <= 0 ? 'text-red-600' : 'text-teal-700'
                          }`}
                        >
                          {formatARNumber(Number(frac.source_current_stock ?? 0) / 1000)} kg
                        </span>
                        <span className="block text-[11px] text-taupe-400">
                          ≈ {Number(frac.available_units ?? 0)} u.
                        </span>
                      </button>
                    )}
                  </div>
                  {/* Acciones */}
                  <div className="flex items-center gap-1.5 md:justify-end col-span-2 md:col-span-1">
                    <button
                      onClick={() => openEdit(frac)}
                      title="Editar"
                      className="p-2 rounded-lg bg-cream-100 hover:bg-cream-200 text-taupe-700"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(frac)}
                      title="Eliminar"
                      className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modal crear/editar */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editing ? 'Editar fraccionado' : 'Nuevo fraccionado'}
        size="xl"
      >
        <div className="space-y-4">
          {/* 1) Producto padre (granel) — con buscador: el desplegable con todo el
                catálogo era inusable apenas hay unas decenas de productos. */}
          <div>
            <label className="block text-sm font-medium text-taupe-700 mb-1.5">
              Producto a granel (padre) *
            </label>

            {parent ? (
              <div className="flex items-center justify-between gap-3 rounded-xl border border-teal-200 bg-teal-50 px-3 py-2.5">
                <div className="min-w-0">
                  <p className="font-medium text-taupe-800">{parent.name}</p>
                  <p className="text-xs text-taupe-500">
                    Stock: {formatARNumber(parentStockGrams / 1000)} kg · Costo por kg:{' '}
                    {formatPrice(costPerGram * 1000)}
                  </p>
                </div>
                {!editing && (
                  <button
                    type="button"
                    onClick={() => { onSelectParent(undefined); setParentSearch(''); }}
                    className="flex-shrink-0 text-sm text-teal-700 hover:text-teal-900 font-medium"
                  >
                    Cambiar
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-taupe-400" />
                  <input
                    type="text"
                    placeholder="Buscar el producto a granel…"
                    value={parentSearch}
                    onChange={(e) => setParentSearch(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-cream-300 rounded-xl text-sm bg-white focus:outline-none focus:border-taupe-400"
                  />
                </div>
                <div className="mt-2 max-h-44 overflow-y-auto rounded-xl border border-cream-200 divide-y divide-cream-100">
                  {parentResults.length === 0 ? (
                    <p className="text-xs text-taupe-500 px-3 py-3">
                      {parentOptions.length === 0
                        ? 'No hay productos a granel (por peso). Creá primero el producto padre en kilos.'
                        : 'Ningún producto a granel coincide con la búsqueda.'}
                    </p>
                  ) : (
                    parentResults.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => onSelectParent(p.id)}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-cream-50"
                      >
                        <span className="text-sm text-taupe-800">{p.name}</span>
                        <span className="text-xs text-taupe-500 whitespace-nowrap">
                          {formatARNumber(Number(p.current_stock ?? 0) / 1000)} kg
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          {/* 2) Lo que el padre ya tiene cargado, para no duplicar presentaciones */}
          {parent && parentFractions.length > 0 && (
            <div className="rounded-xl bg-cream-50 border border-cream-200 px-3 py-2.5">
              <p className="text-xs text-taupe-600 mb-1.5">
                Este producto ya tiene {parentFractions.length} presentación
                {parentFractions.length === 1 ? '' : 'es'}:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {parentFractions.map((f) => (
                  <span
                    key={f.id}
                    title={f.name}
                    className="text-xs bg-white border border-cream-300 text-taupe-700 px-2 py-0.5 rounded-full"
                  >
                    {f.grams >= 1000 ? `${f.grams / 1000} kg` : `${f.grams} g`}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* 3) Modo: paquetes nuevos o un producto que ya existe */}
          {!editing && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setMode('new')}
                className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium border ${
                  mode === 'new'
                    ? 'bg-teal-600 text-white border-teal-600'
                    : 'bg-white text-taupe-700 border-cream-300 hover:bg-cream-50'
                }`}
              >
                Crear paquetes nuevos
              </button>
              <button
                type="button"
                onClick={() => setMode('existing')}
                className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium border ${
                  mode === 'existing'
                    ? 'bg-teal-600 text-white border-teal-600'
                    : 'bg-white text-taupe-700 border-cream-300 hover:bg-cream-50'
                }`}
              >
                Usar un producto que ya tengo
              </button>
            </div>
          )}

          {/* 4a) ALTA MÚLTIPLE: varias presentaciones del mismo padre de una sola vez */}
          {isMulti && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-taupe-600">Agregar rápido:</span>
                {GRAM_PRESETS.map((g) => (
                  <button
                    key={g}
                    type="button"
                    disabled={!parent}
                    onClick={() => addRow(g)}
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold border disabled:opacity-40 ${
                      existingGrams.has(g)
                        ? 'bg-amber-50 text-amber-700 border-amber-300'
                        : 'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100'
                    }`}
                    title={existingGrams.has(g) ? 'El padre ya tiene esta presentación' : undefined}
                  >
                    {g >= 1000 ? `${g / 1000} kg` : `${g} g`}
                    {existingGrams.has(g) ? ' ✓' : ''}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={!parent}
                  onClick={() => addRow()}
                  className="px-2.5 py-1 rounded-full text-xs font-semibold border border-cream-300 text-taupe-700 hover:bg-cream-50 disabled:opacity-40"
                >
                  + fila vacía
                </button>
              </div>

              {!parent ? (
                <p className="text-sm text-taupe-500">Elegí primero el producto a granel.</p>
              ) : rows.length === 0 ? (
                <p className="text-sm text-taupe-500">
                  Agregá una presentación con los botones de arriba.
                </p>
              ) : (
                <div className="space-y-2">
                  {rows.map((row) => {
                    const g = parseFloat(row.grams) || 0;
                    const precio = parseARNumber(row.price) ?? 0;
                    const costo = costPerGram * g;
                    const ganancia = precio - costo;
                    const duplicada = g > 0 && existingGrams.has(Math.round(g));
                    return (
                      <div
                        key={row.id}
                        className="rounded-xl border border-cream-200 p-3 space-y-2 bg-white"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_110px_150px_auto] gap-2 items-start">
                          <input
                            type="text"
                            placeholder="Nombre del paquete"
                            value={row.name}
                            onChange={(e) =>
                              updateRow(row.id, { name: e.target.value, nameTouched: true })}
                            className="w-full px-3 py-2 border border-cream-300 rounded-xl text-sm bg-white focus:outline-none focus:border-taupe-400"
                          />
                          <input
                            type="number"
                            min="0"
                            step="1"
                            placeholder="gramos"
                            value={row.grams}
                            onChange={(e) => onRowGramsChange(row, e.target.value)}
                            className={`w-full px-3 py-2 border rounded-xl text-sm bg-white focus:outline-none ${
                              duplicada ? 'border-amber-400' : 'border-cream-300 focus:border-taupe-400'
                            }`}
                          />
                          <CurrencyInput
                            value={row.price}
                            onChange={(v) => updateRow(row.id, { price: v })}
                          />
                          <button
                            type="button"
                            onClick={() => removeRow(row.id)}
                            title="Quitar esta presentación"
                            className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 justify-self-start"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>

                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                          {g > 0 && (
                            <>
                              <span className="text-taupe-500">
                                Costo: <strong className="text-taupe-700">{formatPrice(costo)}</strong>
                              </span>
                              {precio > 0 && (
                                <span className="text-taupe-500">
                                  Ganancia:{' '}
                                  <strong className={ganancia >= 0 ? 'text-green-600' : 'text-red-600'}>
                                    {formatPrice(ganancia)}
                                  </strong>
                                </span>
                              )}
                              <span className="text-taupe-500">
                                Salen{' '}
                                <strong className="text-taupe-700">
                                  {Math.floor(parentStockGrams / g)}
                                </strong>{' '}
                                con el stock actual
                              </span>
                            </>
                          )}
                          <div className="ml-auto flex items-center gap-2">
                            {row.code ? (
                              <span className="font-mono text-taupe-500">{row.code}</span>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => updateRow(row.id, { code: genInternalBarcode() })}
                              className="px-2 py-0.5 rounded-lg bg-teal-50 text-teal-700 hover:bg-teal-100 font-medium"
                              title="Generar código de barras interno (EAN-13)"
                            >
                              {row.code ? 'Regenerar código' : 'Generar código'}
                            </button>
                          </div>
                        </div>

                        {duplicada && (
                          <p className="text-xs text-amber-700">
                            El padre ya tiene una presentación de {Math.round(g)} g.
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* 4b) PRODUCTO EXISTENTE: se le asigna el padre, no se crea nada nuevo */}
          {!editing && mode === 'existing' && (
            <div className="space-y-3">
              {linkedProduct ? (
                <div className="flex items-center justify-between gap-3 rounded-xl border border-cream-300 bg-cream-50 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="font-medium text-taupe-800">{linkedProduct.name}</p>
                    <p className="text-xs text-taupe-500">
                      Pasa a descontar del stock de {parent?.name ?? 'el padre'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setLinkedProduct(null); setLinkSearch(''); }}
                    className="flex-shrink-0 text-sm text-teal-700 hover:text-teal-900 font-medium"
                  >
                    Cambiar
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-taupe-400" />
                    <input
                      type="text"
                      placeholder="Buscar el producto que ya tenés cargado…"
                      value={linkSearch}
                      onChange={(e) => setLinkSearch(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-cream-300 rounded-xl text-sm bg-white focus:outline-none focus:border-taupe-400"
                    />
                  </div>
                  {linkSearch.trim() && (
                    <div className="max-h-44 overflow-y-auto rounded-xl border border-cream-200 divide-y divide-cream-100">
                      {linkResults.length === 0 ? (
                        <p className="text-xs text-taupe-500 px-3 py-3">
                          Ningún producto coincide con la búsqueda.
                        </p>
                      ) : (
                        linkResults.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => onSelectLinked(p)}
                            className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-cream-50"
                          >
                            <span className="text-sm text-taupe-800">{p.name}</span>
                            <span className="text-xs text-green-700 font-semibold whitespace-nowrap">
                              {formatPrice(p.sale_price ?? 0)}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* 4c) FORM DE A UNO: al editar, o al asociar un producto existente */}
          {(editing || (mode === 'existing' && linkedProduct)) && (
            <>
              <Input
                label="Nombre del paquete *"
                placeholder="Ej: Nueces 250g"
                value={form.name}
                onChange={(e) => updateForm({ name: e.target.value })}
              />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-taupe-700 mb-1.5">
                    Gramos por paquete *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    placeholder="250"
                    value={form.grams}
                    onChange={(e) => updateForm({ grams: e.target.value })}
                    className="w-full px-3 py-2 border border-cream-300 rounded-xl text-sm bg-white focus:outline-none focus:border-taupe-400"
                  />
                  {gramsNum > 0 && existingGrams.has(Math.round(gramsNum))
                    && Number(editing?.fraction_grams ?? -1) !== Math.round(gramsNum) && (
                    <p className="text-xs text-amber-700 mt-1">
                      El padre ya tiene una presentación de {Math.round(gramsNum)} g.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-taupe-700 mb-1.5">
                    Código de barras (propio)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Ej: NUE-250"
                      value={form.code}
                      onChange={(e) => updateForm({ code: e.target.value })}
                      className="flex-1 min-w-0 px-3 py-2 border border-cream-300 rounded-xl text-sm bg-white focus:outline-none focus:border-taupe-400"
                    />
                    <button
                      type="button"
                      onClick={() => updateForm({ code: genInternalBarcode() })}
                      className="flex-shrink-0 px-3 py-2 rounded-xl bg-teal-50 text-teal-700 hover:bg-teal-100 text-sm font-medium"
                      title="Generar código de barras interno (EAN-13)"
                    >
                      Generar
                    </button>
                  </div>
                  <p className="text-xs text-taupe-400 mt-1">
                    Cada paquete lleva su propio código (tienen distinto precio). No se hereda del padre.
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-taupe-700 mb-1.5">Precio de venta *</label>
                <CurrencyInput value={form.price_cash} onChange={(v) => updateForm({ price_cash: v })} />
              </div>

              {/* Costo y ganancia */}
              {gramsNum > 0 && parent && (
                <div className="bg-cream-50 border border-cream-200 rounded-xl p-4 space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-taupe-600">Costo del paquete (auto desde el padre):</span>
                    <span className="font-semibold text-taupe-800">{formatPrice(fracCost)}</span>
                  </div>
                  {cashPriceNum > 0 && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-taupe-600">Ganancia:</span>
                        <span className={`font-semibold ${fracProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatPrice(fracProfit)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-taupe-600">% de ganancia:</span>
                        <span className={`font-bold ${fracProfit >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                          {fracMarkupPct.toFixed(0)}% sobre costo
                          <span className="text-taupe-400 font-normal"> · {fracMarginPct.toFixed(0)}% margen</span>
                        </span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-taupe-600">Paquetes posibles con el stock actual:</span>
                    <span className="font-semibold text-taupe-700">{possiblePackages}</span>
                  </div>
                  {costPerGram <= 0 && (
                    <p className="text-xs text-amber-600">
                      El producto padre no tiene costo cargado: el costo mostrado es 0.
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          <p className="text-xs text-taupe-400">
            El fraccionado no lleva stock propio: al venderlo se descuentan los gramos del
            paquete del stock del padre. El costo se actualiza solo si cambia el costo del padre.
          </p>

          {/* Acciones */}
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" className="flex-1" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              className="flex-1 bg-teal-600 hover:bg-teal-700"
              disabled={!isValid || createProduct.isPending || updateProduct.isPending || savingRows}
              onClick={handleSave}
            >
              <Check className="h-5 w-5 mr-2" />
              {editing
                ? 'Guardar cambios'
                : isMulti
                ? `Crear ${validRows.length || ''} fraccionado${validRows.length === 1 ? '' : 's'}`
                : 'Convertir en fraccionado'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal eliminar */}
      <Modal isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Eliminar fraccionado">
        <div className="space-y-4">
          <p>
            ¿Eliminar el fraccionado <strong>{deleteConfirm?.name}</strong>?
          </p>
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setDeleteConfirm(null)}>
              No
            </Button>
            <Button variant="danger" className="flex-1" onClick={handleDelete} disabled={deleteProduct.isPending}>
              Sí, eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
